// Canvas drawing helpers for banner PNG generation
import { NAVY, GOLD, CREAM } from './config';

const drawRoundedRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
};

const wrapText = (ctx, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let line = '';
    for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = word;
        } else {
            line = test;
        }
    }
    if (line) lines.push(line);
    return lines;
};

const drawBackground = (ctx, w, h, layout) => {
    if (layout === 'portrait') {
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, NAVY);
        grad.addColorStop(1, '#1a2d52');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
    } else if (layout === 'card') {
        ctx.fillStyle = CREAM;
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = NAVY;
        ctx.fillRect(0, 0, w * 0.42, h);
    } else {
        // landscape
        ctx.fillStyle = CREAM;
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = NAVY;
        ctx.fillRect(0, 0, w, h * 0.22);
        ctx.fillStyle = GOLD;
        ctx.fillRect(0, h * 0.22, w, 8);
    }
};

const drawBranding = (ctx, w, h, layout) => {
    ctx.save();
    if (layout === 'portrait') {
        ctx.fillStyle = GOLD;
        ctx.font = 'bold 36px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('SCALIMMO', w / 2, 90);
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.font = '24px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.fillText('La plateforme des agents immobiliers', w / 2, 130);
    } else if (layout === 'landscape') {
        ctx.fillStyle = GOLD;
        ctx.font = 'bold 44px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('SCALIMMO', 60, 95);
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.font = '22px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.fillText('La plateforme des agents immobiliers', 60, 135);
        ctx.fillStyle = GOLD;
        ctx.textAlign = 'right';
        ctx.font = 'bold 28px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.fillText('★ AGENT VÉRIFIÉ', w - 60, 110);
    } else {
        ctx.fillStyle = GOLD;
        ctx.font = 'bold 30px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('SCALIMMO', 50, 70);
        ctx.fillStyle = 'rgba(255,255,255,0.65)';
        ctx.font = '15px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.fillText('Agent vérifié', 50, 95);
    }
    ctx.restore();
};

const computeQRPlacement = (w, h, layout) => {
    if (layout === 'portrait') {
        const size = Math.floor(w * 0.62);
        return { size, x: (w - size) / 2, y: Math.floor(h * 0.46) };
    }
    if (layout === 'landscape') {
        const size = Math.floor(h * 0.6);
        return { size, x: w - size - 80, y: (h - size) / 2 + 40 };
    }
    // card
    const size = Math.floor(h * 0.65);
    return { size, x: (w * 0.42 - size) / 2, y: (h - size) / 2 + 30 };
};

const drawQR = (ctx, qrImg, qrX, qrY, qrSize) => {
    ctx.save();
    ctx.fillStyle = '#ffffff';
    drawRoundedRect(ctx, qrX - 24, qrY - 24, qrSize + 48, qrSize + 48, 28);
    ctx.shadowColor = 'rgba(0,0,0,0.18)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetY = 10;
    ctx.fill();
    ctx.restore();
    if (qrImg) ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
};

const drawTextPortrait = (ctx, w, h, qrY, qrSize, fullName, agency, city, tagline) => {
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 64px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.textAlign = 'center';
    const lines = wrapText(ctx, tagline.toUpperCase(), w - 120);
    lines.forEach((ln, i) => ctx.fillText(ln, w / 2, 280 + i * 76));

    ctx.fillStyle = GOLD;
    ctx.font = '36px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.fillText('▼ Scannez le QR ▼', w / 2, 280 + lines.length * 76 + 60);

    const nameY = qrY + qrSize + 100;
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 56px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.fillText(fullName, w / 2, nameY);
    if (agency || city) {
        ctx.fillStyle = 'rgba(255,255,255,0.75)';
        ctx.font = '32px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.fillText([agency, city].filter(Boolean).join(' · '), w / 2, nameY + 50);
    }
    ctx.fillStyle = GOLD;
    ctx.font = 'bold 30px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.fillText('PRENDRE RDV EN 1 MINUTE', w / 2, h - 100);
};

const drawTextLandscape = (ctx, w, h, qrSize, fullName, agency, city) => {
    const leftX = 70;
    const startY = h * 0.32;
    ctx.fillStyle = NAVY;
    ctx.textAlign = 'left';
    ctx.font = 'bold 96px "Helvetica Neue", Helvetica, Arial, sans-serif';
    const lines = wrapText(ctx, 'SCANNEZ POUR VOIR MON PLANNING', w - qrSize - 220);
    lines.forEach((ln, i) => ctx.fillText(ln, leftX, startY + i * 110));

    const nameY = startY + lines.length * 110 + 60;
    ctx.fillStyle = GOLD;
    ctx.font = 'bold 56px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.fillText(fullName, leftX, nameY);
    if (agency || city) {
        ctx.fillStyle = '#5a6a8a';
        ctx.font = '36px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.fillText([agency, city].filter(Boolean).join(' · '), leftX, nameY + 50);
    }

    ctx.fillStyle = GOLD;
    ctx.fillRect(0, h - 80, w, 80);
    ctx.fillStyle = NAVY;
    ctx.font = 'bold 32px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('PRENDRE RDV EN 1 MINUTE · SCALIMMO.FR', w / 2, h - 30);
};

const drawTextCard = (ctx, w, h, fullName, agency, city) => {
    const rightX = w * 0.42 + 50;
    ctx.fillStyle = NAVY;
    ctx.textAlign = 'left';
    ctx.font = 'bold 44px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.fillText(fullName, rightX, 110);
    ctx.fillStyle = GOLD;
    ctx.font = '22px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.fillText('AGENT IMMOBILIER', rightX, 145);
    if (agency) {
        ctx.fillStyle = NAVY;
        ctx.font = '24px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.fillText(agency, rightX, 195);
    }
    if (city) {
        ctx.fillStyle = '#5a6a8a';
        ctx.font = '20px "Helvetica Neue", Helvetica, Arial, sans-serif';
        ctx.fillText(`📍 ${city}`, rightX, 230);
    }
    ctx.fillStyle = NAVY;
    ctx.font = 'bold 30px "Helvetica Neue", Helvetica, Arial, sans-serif';
    const lines = wrapText(ctx, 'Scannez pour prendre RDV', w - rightX - 50);
    lines.forEach((ln, i) => ctx.fillText(ln, rightX, 320 + i * 38));
    ctx.fillStyle = GOLD;
    ctx.font = 'bold 18px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.fillText('▶ SCALIMMO.FR', rightX, h - 50);
};

export const drawBanner = ({ ctx, w, h, layout, fullName, agency, city, qrImg, tagline }) => {
    drawBackground(ctx, w, h, layout);
    drawBranding(ctx, w, h, layout);

    const { size, x, y } = computeQRPlacement(w, h, layout);
    drawQR(ctx, qrImg, x, y, size);

    if (layout === 'portrait') drawTextPortrait(ctx, w, h, y, size, fullName, agency, city, tagline);
    else if (layout === 'landscape') drawTextLandscape(ctx, w, h, size, fullName, agency, city);
    else drawTextCard(ctx, w, h, fullName, agency, city);
};
